import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";

// Paths we treat as internal and route through React Router. Anything else
// (http://…) opens in a new tab.
const INTERNAL_PATH_RE = /(\/(?:product|shop|journal|rooms|arrivals|favourites|cart|orders|auth)(?:\/[a-zA-Z0-9_-]+)?)/g;
// [label](/path) or [label](https://…) — capture 2 groups, label + url.
const MD_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
// **bold** rendered as <strong>.
const MD_BOLD_RE = /\*\*([^*]+)\*\*/g;
// Any /product/<slug> reference (bare or inside a markdown link).
const PRODUCT_SLUG_RE = /\/product\/([a-z0-9-]+)/gi;

const linkStyle = {
  color: "#f0d38a",
  textDecoration: "underline",
  textDecorationColor: "rgba(212,175,55,0.55)",
  textUnderlineOffset: "3px",
};

const renderLink = (link, key, onLinkClick, invalidSlugs) => {
  if (!link) return null;
  // If this link points to a product that doesn't exist in the catalogue
  // (LLM hallucination), demote to plain bold text so shoppers aren't sent
  // to a 404 page.
  if (invalidSlugs && invalidSlugs.size) {
    const productMatch = link.url.match(/^\/product\/([a-z0-9-]+)/i);
    if (productMatch && invalidSlugs.has(productMatch[1])) {
      return (
        <strong key={key} style={{ color: "#f0d38a" }} title="Not currently in stock">
          {link.label}
        </strong>
      );
    }
  }
  const isInternal = link.url.startsWith("/");
  if (isInternal) {
    return (
      <Link
        key={key}
        to={link.url}
        onClick={() => onLinkClick?.(link.url)}
        data-testid={`ai-link-${link.url.replace(/\//g, "-").replace(/^-/, "")}`}
        style={linkStyle}
        className="hover:brightness-125"
      >
        {link.label}
      </Link>
    );
  }
  return (
    <a
      key={key}
      href={link.url}
      target="_blank"
      rel="noreferrer"
      style={linkStyle}
      className="hover:brightness-125"
    >
      {link.label}
    </a>
  );
};

const renderBoldedText = (text, keyBase) => {
  const nodes = [];
  let lastIndex = 0;
  let m;
  const re = new RegExp(MD_BOLD_RE.source, "g");
  let idx = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) nodes.push(text.slice(lastIndex, m.index));
    nodes.push(
      <strong key={`${keyBase}-b-${idx++}`} style={{ color: "#f0d38a" }}>
        {m[1]}
      </strong>,
    );
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes.length ? nodes : [text];
};

/**
 * Render a plain-text AI-generated message with:
 *   1. `[label](url)` markdown → clickable link
 *   2. bare `/product/slug`, `/shop/…`, `/journal/…`, `/rooms` → clickable link
 *   3. `**bold**` → <strong>
 *
 * Options:
 *   `invalidSlugs` — a Set<string> of product slugs that don't exist in the
 *   catalogue. Any link pointing to `/product/<slug>` in this set is demoted
 *   to plain bold text so shoppers aren't sent to a broken page.
 *
 * Internal links route via React Router. `onLinkClick(url)` fires so the
 * caller can close any surrounding drawer / modal before navigation.
 */
export const renderAIMessage = (raw, onLinkClick, { invalidSlugs } = {}) => {
  if (!raw) return null;

  // Pass 1 — extract [label](url) into placeholders so their body text can't
  // accidentally match the bare-path or bold passes.
  const mdLinks = [];
  const withPlaceholders = raw.replace(MD_LINK_RE, (_, label, url) => {
    mdLinks.push({ label, url });
    return `\uE000${mdLinks.length - 1}\uE001`;
  });

  // Pass 2 — split on bare internal paths.
  const parts = [];
  let remaining = withPlaceholders;
  let m;
  const combined = new RegExp(INTERNAL_PATH_RE.source, "g");
  let lastIndex = 0;
  while ((m = combined.exec(remaining)) !== null) {
    if (m.index > lastIndex) parts.push({ text: remaining.slice(lastIndex, m.index) });
    parts.push({ link: { label: m[1], url: m[1] } });
    lastIndex = m.index + m[1].length;
  }
  if (lastIndex < remaining.length) parts.push({ text: remaining.slice(lastIndex) });

  // Pass 3 — expand markdown-link placeholders + render bold + text.
  const nodes = [];
  parts.forEach((p, i) => {
    if (p.link) {
      nodes.push(renderLink(p.link, `bare-${i}`, onLinkClick, invalidSlugs));
      return;
    }
    const chunks = p.text.split(/(\uE000\d+\uE001)/g);
    chunks.forEach((chunk, j) => {
      const ph = chunk.match(/^\uE000(\d+)\uE001$/);
      if (ph) {
        nodes.push(renderLink(mdLinks[parseInt(ph[1], 10)], `md-${i}-${j}`, onLinkClick, invalidSlugs));
      } else if (chunk) {
        nodes.push(...renderBoldedText(chunk, `t-${i}-${j}`));
      }
    });
  });
  return nodes;
};

// -----------------------------------------------------------------------
// Product preview mini-cards
// -----------------------------------------------------------------------
// In-memory cache keyed by slug so re-renders and repeat mentions don't
// re-fetch the same product. Lives for the tab lifetime.
const _productCache = new Map();

const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

/** Extract every unique product slug referenced in a message body. */
export const extractProductSlugs = (raw) => {
  if (!raw) return [];
  const slugs = new Set();
  let m;
  const re = new RegExp(PRODUCT_SLUG_RE.source, "gi");
  while ((m = re.exec(raw)) !== null) {
    slugs.add(m[1]);
  }
  return Array.from(slugs);
};

/**
 * Compact product mini-card rendered below an AI message bubble. Fetches the
 * product on mount, caches by slug, and stays quiet if the product 404s.
 */
export const ConciergeProductCard = ({ slug, onOpen }) => {
  const [product, setProduct] = useState(() => _productCache.get(slug) || null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (product || missing) return;
    let cancelled = false;
    api.get(`/products/${slug}`)
      .then((r) => {
        if (cancelled) return;
        _productCache.set(slug, r.data);
        setProduct(r.data);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      });
    return () => { cancelled = true; };
  }, [slug, product, missing]);

  if (missing || !product) return null;

  return (
    <Link
      to={`/product/${product.slug}`}
      onClick={() => onOpen?.(`/product/${product.slug}`)}
      data-testid={`concierge-product-card-${product.slug}`}
      className="flex gap-3 items-center px-3 py-2 mt-2 transition hover:brightness-110"
      style={{
        background: "linear-gradient(180deg, rgba(30,20,18,0.9) 0%, rgba(18,12,12,0.9) 100%)",
        border: "1px solid rgba(212,175,55,0.35)",
        borderLeft: "2px solid rgba(212,175,55,0.65)",
        textDecoration: "none",
      }}
    >
      <img
        src={resolveImg(product.image)}
        alt={product.name}
        loading="lazy"
        className="w-14 h-14 object-cover shrink-0"
        style={{ border: "1px solid rgba(212,175,55,0.25)" }}
      />
      <div className="flex-1 min-w-0">
        <div
          className="font-serif-display text-sm leading-tight text-bone truncate"
          style={{ color: "#f5efe0" }}
          title={product.name}
        >
          {product.name}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span
            className="text-xs font-mono"
            style={{ color: "#d4af37" }}
          >
            ${Number(product.price).toFixed(2)}
          </span>
          {product.category && (
            <span className="text-[9px] uppercase tracking-[0.22em] text-muted-bone/70">
              · {product.category}
            </span>
          )}
        </div>
      </div>
      <span
        className="text-[10px] uppercase tracking-[0.2em] shrink-0 px-2"
        style={{ color: "#d4af37" }}
      >
        View →
      </span>
    </Link>
  );
};
